import { requestNavigationConfirmation } from './navigationConfirmation';

// Cached panels have local locations, but navigation uses the real router.
// Share one outer-history prompt so hidden panels cannot overwrite active guards.
const outerBlockers = new WeakMap();

function registerPanelBlocker(outerHistory, prompt) {
  let registry = outerBlockers.get(outerHistory);
  if (!registry) {
    const prompts = new Set();
    const evaluate = (location, action) => {
      for (const candidate of Array.from(prompts).reverse()) {
        const result = candidate(location, action);
        if (result === false || typeof result === 'string') return result;
      }
      return undefined;
    };
    registry = { prompts, evaluate, suspended: false };
    registry.unblock = outerHistory.block((location, action) => registry.suspended ? undefined : evaluate(location, action));
    outerBlockers.set(outerHistory, registry);
  }
  registry.prompts.add(prompt);
  return () => {
    registry.prompts.delete(prompt);
    if (registry.prompts.size === 0 && outerBlockers.get(outerHistory) === registry) {
      registry.unblock();
      outerBlockers.delete(outerHistory);
    }
  };
}

export const createDashboardPanelHistory = (initialOuterHistory, initialLocation, initialIsActive) => {
  const listeners = new Set();
  const snapshot = {
    outerHistory: initialOuterHistory,
    location: initialLocation,
    isActive: initialIsActive,
    action: initialOuterHistory.action || 'POP',
  };

  const runIfActive = (callback) => {
    if (snapshot.isActive) {
      return callback(snapshot.outerHistory);
    }
    return undefined;
  };

  const unregisterBlocks = new Set();

  const history = {
    get length() {
      return snapshot.outerHistory.length;
    },
    get action() {
      return snapshot.action;
    },
    get location() {
      return snapshot.location;
    },
    createHref(location) {
      return snapshot.outerHistory.createHref(location);
    },
    push(location, state) {
      return runIfActive((outerHistory) => outerHistory.push(location, state));
    },
    replace(location, state) {
      return runIfActive((outerHistory) => outerHistory.replace(location, state));
    },
    go(n) {
      return runIfActive((outerHistory) => outerHistory.go(n));
    },
    goBack() {
      return runIfActive((outerHistory) => outerHistory.goBack());
    },
    goForward() {
      return runIfActive((outerHistory) => outerHistory.goForward());
    },
    listen(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    runGuarded(commit) {
      if (!snapshot.isActive) return;
      const registry = outerBlockers.get(snapshot.outerHistory);
      const decision = registry?.evaluate(snapshot.location, 'PUSH');
      const proceed = allowed => {
        if (!allowed || !snapshot.isActive) return;
        // Tab metadata must commit before the URL, but only after consent.
        // Avoid asking the same guard again when commit pushes the URL.
        if (registry) registry.suspended = true;
        try { commit(); } finally { if (registry) registry.suspended = false; }
      };
      if (typeof decision === 'string') requestNavigationConfirmation(decision, proceed);
      else proceed(decision !== false);
    },
    block(prompt) {
      const unregister = registerPanelBlocker(snapshot.outerHistory, (location, action) => {
        if (!snapshot.isActive) return undefined;
        return typeof prompt === 'function' ? prompt(location, action) : prompt;
      });
      const cleanup = () => {
        unregister();
        unregisterBlocks.delete(cleanup);
      };
      unregisterBlocks.add(cleanup);
      return cleanup;
    },
  };

  return {
    history,
    dispose() {
      unregisterBlocks.forEach((unregister) => unregister());
      listeners.clear();
    },
    setSnapshot(nextSnapshot) {
      snapshot.outerHistory = nextSnapshot.outerHistory;
      snapshot.location = nextSnapshot.location;
      snapshot.isActive = nextSnapshot.isActive;
      snapshot.action = nextSnapshot.action || snapshot.outerHistory.action || 'POP';
    },
    notify() {
      listeners.forEach((listener) => listener(snapshot.location, snapshot.action));
    },
  };
};
